import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format, parse, isValid } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarIcon, X, User as UserIcon, Sparkles, Lock, Globe } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";
import { COUNTRIES } from "@/lib/countries";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GENDER_LABEL = {
    "": "Not set",
    female: "Female",
    male: "Male",
    nonbinary: "Non-binary",
    prefer_not_to_say: "Prefer not to say",
};

export default function Profile() {
    const { setUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [gender, setGender] = useState("");
    const [dob, setDob] = useState(""); // YYYY-MM-DD or ""
    const [country, setCountry] = useState("");
    const [dateOpen, setDateOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    // Change password state
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [changingPw, setChangingPw] = useState(false);

    const changePassword = async (e) => {
        e.preventDefault();
        if (newPw.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        if (newPw !== confirmPw) {
            toast.error("New passwords do not match");
            return;
        }
        setChangingPw(true);
        try {
            await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
            toast.success("Password updated");
            setCurrentPw(""); setNewPw(""); setConfirmPw("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not change password");
        } finally {
            setChangingPw(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/profile");
                setProfile(data);
                setGender(data.gender || "");
                setDob(data.dob || "");
                setCountry(data.country || "");
            } catch {
                toast.error("Could not load your profile");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const dobDate = dob ? parse(dob, "yyyy-MM-dd", new Date()) : null;

    const save = async () => {
        if (!dob) {
            toast.error("Date of birth is required");
            return;
        }
        if (!country) {
            toast.error("Country/Region is required");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch("/profile", { gender, dob, country });
            setProfile(data);
            setUser({ user_id: data.user_id, email: data.email, name: data.name, picture: data.picture, is_admin: data.is_admin });
            toast.success("Profile updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const clearDob = () => { setDob(""); setDateOpen(false); };
    const clearGender = () => setGender("");

    if (loading) {
        return <div className="p-10 text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="max-w-3xl mx-auto p-6 sm:p-10 lg:p-14">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-medium">Profile</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight">
                    A little about you. <span className="font-[300] italic">Only if you want.</span>
                </h1>
                <p className="text-muted-foreground mt-3">Everything here is optional. You can add, change, or clear these details anytime.</p>
            </motion.div>

            {/* Read-only info */}
            <div className="bg-card rounded-3xl p-8 border border-border shadow-soft mb-6" data-testid="profile-basics-card">
                <div className="flex items-center gap-2 mb-6">
                    <UserIcon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-widest">Account</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-widest">Name</Label>
                        <div className="mt-2 text-lg font-medium">{profile?.name || "—"}</div>
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-widest">Email</Label>
                        <div className="mt-2 text-lg font-medium truncate">{profile?.email}</div>
                    </div>
                </div>
            </div>

            {/* Editable profile info */}
            <div className="bg-card rounded-3xl p-8 border border-border shadow-soft" data-testid="profile-optional-card">
                <div className="flex items-center gap-2 mb-6">
                    <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-widest">Your details</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {/* Gender (optional) */}
                    <div>
                        <Label className="text-sm font-medium mb-2 block">
                            Gender <span className="text-muted-foreground font-normal">(optional)</span>
                        </Label>
                        <div className="flex gap-2">
                            <Select value={gender || "unset"} onValueChange={(v) => setGender(v === "unset" ? "" : v)}>
                                <SelectTrigger className="rounded-2xl bg-background h-12" data-testid="profile-gender-select">
                                    <SelectValue placeholder="Not set">{GENDER_LABEL[gender] || "Not set"}</SelectValue>
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                    <SelectItem value="unset">Not set</SelectItem>
                                    <SelectItem value="female" data-testid="gender-female">Female</SelectItem>
                                    <SelectItem value="male" data-testid="gender-male">Male</SelectItem>
                                    <SelectItem value="nonbinary" data-testid="gender-nonbinary">Non-binary</SelectItem>
                                    <SelectItem value="prefer_not_to_say" data-testid="gender-prefer-not">Prefer not to say</SelectItem>
                                </SelectContent>
                            </Select>
                            {gender && (
                                <Button variant="ghost" size="icon" onClick={clearGender} className="h-12 w-12 rounded-2xl flex-shrink-0" data-testid="profile-clear-gender" aria-label="Clear gender">
                                    <X className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* DOB (required) */}
                    <div>
                        <Label className="text-sm font-medium mb-2 block">
                            Date of birth <span className="text-destructive">*</span>
                        </Label>
                        <Popover open={dateOpen} onOpenChange={setDateOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="w-full h-12 rounded-2xl bg-background justify-start font-normal"
                                    data-testid="profile-dob-trigger"
                                >
                                    <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" strokeWidth={1.5} />
                                    {dobDate && isValid(dobDate) ? format(dobDate, "PPP") : "Choose your date of birth"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 rounded-2xl" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dobDate || undefined}
                                    onSelect={(d) => {
                                        if (d) { setDob(format(d, "yyyy-MM-dd")); setDateOpen(false); }
                                    }}
                                    captionLayout="dropdown-buttons"
                                    fromYear={1900}
                                    toYear={new Date().getFullYear()}
                                    defaultMonth={dobDate || new Date(2000, 0)}
                                    disabled={(d) => d > new Date() || d < new Date("1900-01-01")}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Country (required) */}
                    <div className="sm:col-span-2">
                        <Label className="text-sm font-medium mb-2 block">
                            Country / Region <span className="text-destructive">*</span>
                        </Label>
                        <Select value={country || "unset"} onValueChange={(v) => setCountry(v === "unset" ? "" : v)}>
                            <SelectTrigger className="rounded-2xl bg-background h-12" data-testid="profile-country-select">
                                <SelectValue placeholder="Choose your country">
                                    {country ? (COUNTRIES.find(c => c.code === country)?.name || country) : (
                                        <span className="flex items-center gap-2 text-muted-foreground">
                                            <Globe className="w-4 h-4" strokeWidth={1.5} /> Choose your country
                                        </span>
                                    )}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="rounded-2xl max-h-72">
                                <SelectItem value="unset">Not set</SelectItem>
                                {COUNTRIES.map(c => (
                                    <SelectItem key={c.code} value={c.code} data-testid={`country-${c.code}`}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="mt-10 flex items-center gap-3">
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-full h-12 px-8 font-medium shadow-soft"
                        data-testid="profile-save-btn"
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                    <span className="text-xs text-muted-foreground">Date of birth and country are required. Gender is optional.</span>
                </div>
            </div>

            {/* Change password */}
            {profile?.email && (
                <div className="bg-card rounded-3xl p-8 border border-border shadow-soft mt-6" data-testid="change-password-card">
                    <div className="flex items-center gap-2 mb-6">
                        <Lock className="w-4 h-4 text-primary" strokeWidth={1.5} />
                        <span className="text-sm font-medium text-muted-foreground uppercase tracking-widest">Change password</span>
                    </div>
                    <form onSubmit={changePassword} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-3">
                            <Label htmlFor="current-pw" className="text-sm font-medium mb-2 block">Current password</Label>
                            <Input
                                id="current-pw" type="password" required autoComplete="current-password"
                                value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                                placeholder="••••••••"
                                className="h-12 rounded-2xl bg-background max-w-md"
                                data-testid="current-password-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="new-pw" className="text-sm font-medium mb-2 block">New password</Label>
                            <Input
                                id="new-pw" type="password" required autoComplete="new-password"
                                value={newPw} onChange={(e) => setNewPw(e.target.value)}
                                placeholder="At least 6 characters"
                                className="h-12 rounded-2xl bg-background"
                                data-testid="new-password-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="confirm-pw" className="text-sm font-medium mb-2 block">Confirm new password</Label>
                            <Input
                                id="confirm-pw" type="password" required autoComplete="new-password"
                                value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                                placeholder="Repeat new password"
                                className="h-12 rounded-2xl bg-background"
                                data-testid="confirm-password-input"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button
                                type="submit"
                                disabled={changingPw || !currentPw || !newPw || !confirmPw}
                                className="rounded-full h-12 px-8 font-medium shadow-soft w-full"
                                data-testid="change-password-btn"
                            >
                                {changingPw ? "Updating…" : "Update password"}
                            </Button>
                        </div>
                    </form>
                    <p className="text-xs text-muted-foreground mt-4">
                        Google sign-in accounts do not have an app password. If you signed in with Google, please change your password from your Google account.
                    </p>
                </div>
            )}

            <FeedbackFooter />
        </div>
    );
}
